"use client"
import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import SvgPlus from '@/src/assets/svgs/collection/plus.svg'
import SvgMinus from '@/src/assets/svgs/collection/minus.svg'
import BottomSheet from '../../helpers/overlays/bottom-sheet/bottom-sheet'
import { Overlay } from '../../helpers/overlays/overlay'
import ModalAddCustom from '../modal-add-custom/modal-add-custom'
import { useLongPress } from '@/src/hooks/useLongPress'

type Theme = 'primary' | 'default' | 'inverted'
type Border = 'rounded' | 'radius'

interface Props {
    setValue(quantity: number): void
    border?: Border
    theme?: Theme,
    value: number
}

export default function AddMore(props: Props) {
    const [value, setValue] = useState(props.value)
    const [bottomSheet, setBottomSheet] = useState(false)

    const increaseRef = useRef<HTMLDivElement>(null)
    const decreaseRef = useRef<HTMLDivElement>(null)

    const longPress = useLongPress({
        onLongPress: () => setBottomSheet(true),
        delay: 500
    })

    const reachedMinimum = () => {
        return value <= 1
    }

    const reachedMaximum = () => {
        return value > 999
    }

    const increase = (customValue?: number) => {
        if (customValue) {
            setValue(customValue)
            Overlay.dismiss()
        }
        else {
            setValue(value + 1)
        }
    }

    const decrease = () => {
        if (value > 1) {
            setValue(value - 1)
        }
    }

    const getContainerClass = () => {
        switch (props.theme) {
            case 'inverted':
                return styles.inverted
            case 'primary':
                return styles.primary
            default:
                return styles.default
        }
    }

    const getAddIconClass = () => {
        switch (props.theme) {
            case 'inverted':
                return styles.invertedIcon
            case 'primary':
                return styles.primaryIcon
            default:
                return styles.defaultIcon
        }
    }

    const getContainerBorder = () => {
        if (props.border === 'radius')
            return styles.radius
        return styles.rounded
    }

    const getIconBorder = () => {
        if (props.border === 'radius')
            return styles.radiusIcon
        return styles.roundedIcon
    }

    const handleCustomValueConfirm = (customValue: number) => {
        setValue(customValue)
        setBottomSheet(false)
    }

    useEffect(() => {
        props.setValue(value)
        setValue(value)
    }, [value])

    useEffect(() => {
        const cleanupIncrease = longPress.attachListeners(increaseRef.current)
        const cleanupDecrease = longPress.attachListeners(decreaseRef.current)

        return () => {
            cleanupIncrease?.()
            cleanupDecrease?.()
        }
    }, [])

    return (
        <>
            {bottomSheet && (
                <BottomSheet isOpen={bottomSheet} onClose={() => setBottomSheet(false)}>
                    <ModalAddCustom
                        onConfirm={handleCustomValueConfirm}
                        initialValue={value}
                        wizard={false}
                    />
                </BottomSheet>
            )}
            <div className={`${styles.container} ${getContainerClass()} ${getContainerBorder()}`}>
                <div
                    ref={decreaseRef}
                    onClick={decrease}
                    className={`
            ${styles.svgAdd} 
            ${getAddIconClass()} 
            ${getIconBorder()} 
            ${reachedMinimum() ? styles.invalid : styles.valid}
            `}>
                    <SvgMinus />
                </div>
                <span className={`${styles.quantityValue} font-lg`}>{props.value}</span>
                <div
                    ref={increaseRef}
                    onClick={() => increase()}
                    className={`
            ${styles.svgAdd} 
            ${getAddIconClass()} 
            ${getIconBorder()} 
            ${reachedMaximum() ? styles.invalid : styles.valid}
            `}>
                    <SvgPlus />
                </div>
            </div>
        </>
    )
}
